import { makeAutoObservable, runInAction } from 'mobx';

const MAX_OVERTIME_SEC = 30;

/**
 * RotationStore
 * 
 * Centralized management of section timing boundaries and rotation logic.
 * Handles all timing checkpoints: T-20 warning, T0 boundary detection,
 * grace periods, and hard boundary enforcement.
 * 
 * State Machine:
 * - idle: No section active
 * - running: Normal section progress
 * - t20_sent: T-20 warning sent to model
 * - at_t0_both_silent: Hit T0, both parties silent
 * - at_t0_assistant_speaking: Hit T0, assistant speaking (grace period starting)
 * - at_t0_user_speaking: Hit T0, user speaking (wait for user to finish)
 * - grace_active: In grace period after T0
 * - hard_boundary: Hard boundary hit at T0+MAX_OVERTIME_SEC
 * - deferred_rotation: Rotation requested but waiting for assistant audio to finish
 */
class RotationStore {
  // State machine
  state = 'idle';

  // Timing flags
  t20WarningSent = false;
  t0Handled = false;
  hardBoundaryTriggered = false;

  // Timing values
  t0TimestampMs = null;
  graceDeadlineMs = null;

  // Mode flags
  userNaturalWrapMode = false; // true when user was speaking at T0

  constructor(interviewStore, realtimeStore) {
    this.interviewStore = interviewStore;
    this.realtimeStore = realtimeStore;
    makeAutoObservable(this);
  }

  // Computed properties
  get isInGracePeriod() {
    return this.graceDeadlineMs !== null && Date.now() < this.graceDeadlineMs;
  }

  get graceRemainingMs() {
    return this.graceDeadlineMs ? Math.max(0, this.graceDeadlineMs - Date.now()) : 0;
  }

  get userSpeaking() {
    return this.interviewStore._userSpeaking;
  }

  get assistantSpeaking() {
    return this.realtimeStore.assistantSpeaking;
  }

  /**
   * Initialize rotation state for a new section
   */
  startSection() {
    runInAction(() => {
      this.state = 'running';
      this.t20WarningSent = false;
      this.t0Handled = false;
      this.hardBoundaryTriggered = false;
      this.t0TimestampMs = null;
      this.graceDeadlineMs = null;
      this.userNaturalWrapMode = false;
    });
  }

  /**
   * Reset to idle state
   */
  reset() {
    runInAction(() => {
      this.state = 'idle';
      this.t20WarningSent = false;
      this.t0Handled = false;
      this.hardBoundaryTriggered = false;
      this.t0TimestampMs = null;
      this.graceDeadlineMs = null;
      this.userNaturalWrapMode = false;
    });
  }

  /**
   * Called every second from timing loop
   */
  tick(sectionElapsedSec, maxDurationSec) {
    if (this.state === 'idle') return;

    const remaining = maxDurationSec - sectionElapsedSec;

    this._checkT20Warning(remaining);
    this._checkT0Boundary(sectionElapsedSec, maxDurationSec);
    this._checkGracePeriod();
    this._checkHardBoundary(sectionElapsedSec, maxDurationSec);
  }

  /**
   * Called when user speech stops
   */
  onUserSpeechStopped() {
    if (this.userNaturalWrapMode && !this.graceDeadlineMs && !this.hardBoundaryTriggered) {
      this._transitionToGracePeriod('user_stopped');
    }
  }

  /**
   * Called when assistant speech stops (output_audio_buffer.stopped)
   */
  onAssistantSpeechStopped() {
    // If rotation was deferred waiting for audio to stop, execute it now
    if (this.state === 'deferred_rotation') {
      this._executeRotation('deferred_rotation_audio_stopped');
      return;
    }

    // Otherwise, handle grace period logic
    if (this.isInGracePeriod && !this.userNaturalWrapMode) {
      this._attemptRotation('assistant_finished_naturally');
    }
  }

  // ==================== Private Methods ====================

  /**
   * Check if T-20 warning should be sent
   */
  _checkT20Warning(remaining) {
    if (!this.t20WarningSent && remaining <= 20 && remaining > 0) {
      runInAction(() => {
        this.t20WarningSent = true;
        this.state = 't20_sent';
      });
      try {
        this.realtimeStore.service?.sendT20Warning?.();
      } catch (e) {
        console.error('[RotationStore] T-20 warning failed:', e);
      }
    }
  }

  /**
   * Check if we've hit T0 and decide what to do
   */
  _checkT0Boundary(sectionElapsedSec, maxDurationSec) {
    if (this.t0Handled || sectionElapsedSec < maxDurationSec || this.hardBoundaryTriggered) {
      return;
    }

    const assistantSpeaking = this.assistantSpeaking;
    const userSpeaking = this.userSpeaking;

    runInAction(() => {
      this.t0Handled = true;
      this.t0TimestampMs = Date.now();
    });

    if (!userSpeaking && !assistantSpeaking) {
      // Case 3: Both silent at T0 -> send wrap message
      runInAction(() => { this.state = 'at_t0_both_silent'; });
      try {
        this.realtimeStore.service?.sendQuickWrapAndFinishPart?.({ 
          seconds: 4, 
          isInterrupt: false, 
          preemptive: false 
        });
      } catch (e) {
        console.error('[RotationStore] sendQuickWrapAndFinishPart failed:', e);
        this._attemptRotation('both_silent_at_t0_fallback');
      }
    } else if (assistantSpeaking) {
      // Case 2: Assistant speaking at T0 -> let finish naturally, set grace deadline
      runInAction(() => {
        this.state = 'at_t0_assistant_speaking';
        this.graceDeadlineMs = Date.now() + MAX_OVERTIME_SEC * 1000;
      });
      console.log(`[RotationStore] Assistant speaking at T0, grace until ${new Date(this.graceDeadlineMs).toISOString()}`);
    } else {
      // Case 3: User speaking at T0 -> wait for user to stop, then natural wrap
      runInAction(() => {
        this.state = 'at_t0_user_speaking';
        this.userNaturalWrapMode = true;
      });
      console.log('[RotationStore] User speaking at T0, waiting for speech to stop');
    }
  }

  /**
   * Check grace period status and handle expiration
   */
  _checkGracePeriod() {
    if (!this.graceDeadlineMs) return;

    const assistantSpeaking = this.assistantSpeaking;
    const deadlineReached = Date.now() >= this.graceDeadlineMs;

    if (this.userNaturalWrapMode) {
      // User natural wrap mode: wait full grace window, ignore early assistant silence
      if (deadlineReached) {
        if (assistantSpeaking) {
          try {
            this.realtimeStore.service?.cancelActiveResponse?.();
          } catch (e) {
            console.error('[RotationStore] Cancel response failed:', e);
          }
        }
        runInAction(() => {
          this.graceDeadlineMs = null;
          this.userNaturalWrapMode = false;
        });
        this._attemptRotation('grace_deadline_reached_user_mode');
      }
    } else {
      // Assistant grace mode: rotate immediately when assistant stops naturally, or at deadline
      if (!assistantSpeaking || deadlineReached) {
        if (assistantSpeaking && deadlineReached) {
          try {
            this.realtimeStore.service?.cancelActiveResponse?.();
          } catch (e) {
            console.error('[RotationStore] Cancel response failed:', e);
          }
        }
        runInAction(() => { this.graceDeadlineMs = null; });
        this._attemptRotation(assistantSpeaking ? 'grace_deadline_reached_assistant_still_speaking' : 'assistant_stopped_naturally');
      }
    }
  }

  /**
   * Check hard boundary at T0 + MAX_OVERTIME_SEC
   */
  _checkHardBoundary(sectionElapsedSec, maxDurationSec) {
    if (this.hardBoundaryTriggered || sectionElapsedSec < (maxDurationSec + MAX_OVERTIME_SEC)) {
      return;
    }

    runInAction(() => {
      this.hardBoundaryTriggered = true;
      this.state = 'hard_boundary';
    });

    console.log('[RotationStore] Hard boundary hit at T+' + MAX_OVERTIME_SEC);

    const assistantSpeaking = this.assistantSpeaking;
    const userSpeaking = this.userSpeaking;

    if (userSpeaking) {
      // Case 2c: User speaking at hard boundary -> send wrap with interrupt flag
      try {
        this.realtimeStore.service?.sendQuickWrapAndFinishPart?.({ 
          seconds: 4, 
          isInterrupt: true, 
          preemptive: true 
        });
      } catch (e) {
        console.error('[RotationStore] sendQuickWrapAndFinishPart failed:', e);
        this._attemptRotation('hard_boundary_fallback');
      }
    } else if (assistantSpeaking) {
      // Case 1c: Assistant speaking at hard boundary -> cut immediately and rotate
      try {
        this.realtimeStore.service?.cancelActiveResponse?.();
      } catch (e) {
        console.error('[RotationStore] Cancel response failed:', e);
      }
      this._attemptRotation('hard_boundary_assistant_cut');
    } else {
      // Both silent at hard boundary -> just rotate
      this._attemptRotation('hard_boundary_both_silent');
    }
  }

  /**
   * Transition from T0 user-speaking mode to grace period
   * Called when user stops speaking after T0
   */
  _transitionToGracePeriod(reason) {
    console.log(`[RotationStore] Transitioning to grace period: ${reason}`);

    runInAction(() => {
      this.state = 'grace_active';
      this.graceDeadlineMs = Date.now() + MAX_OVERTIME_SEC * 1000;
    });

    // Check if this is the last section
    if (this.interviewStore.currentSection?.isLast) {
      try {
        this.interviewStore.askToFinish({ force: false, reason: 'last-section' });
      } catch (e) {
        console.error('[RotationStore] askToFinish failed:', e);
      }
    } else {
      try {
        this.realtimeStore.service?.armNaturalQuickWrap?.({ 
          seconds: MAX_OVERTIME_SEC, 
          delayMs: 30, 
          fallbackMs: 3000 
        });
      } catch (e) {
        console.error('[RotationStore] armNaturalQuickWrap failed:', e);
      }
    }
  }

  /**
   * Attempt to rotate to next section
   * If assistant is speaking, defer until audio stops to avoid cutting off speech
   */
  _attemptRotation(reason) {
    console.log(`[RotationStore] Attempting rotation: ${reason}`);
    
    // If assistant is speaking (audio buffer active), defer rotation until outputStopped
    if (this.assistantSpeaking) {
      console.log('[RotationStore] Deferring rotation until assistant stops speaking');
      runInAction(() => {
        this.state = 'deferred_rotation';
      });
      // The rotation will be triggered by onAssistantSpeechStopped when audio finishes
      // Add a safety timeout in case outputStopped never fires
      setTimeout(() => {
        if (this.state === 'deferred_rotation') {
          console.log('[RotationStore] Safety timeout: forcing rotation');
          this._executeRotation(reason);
        }
      }, 5000);
      return;
    }
    
    this._executeRotation(reason);
  }

  /**
   * Execute the actual rotation
   */
  _executeRotation(reason) {
    console.log(`[RotationStore] Executing rotation: ${reason}`);
    try {
      this.interviewStore.nextSection({ forceImmediate: true });
    } catch (e) {
      console.error('[RotationStore] Rotation failed:', e);
    }
  }

  /**
   * Debug helper - get current state snapshot
   */
  getDebugInfo() {
    return {
      state: this.state,
      t20WarningSent: this.t20WarningSent,
      t0Handled: this.t0Handled,
      hardBoundaryTriggered: this.hardBoundaryTriggered,
      t0TimestampMs: this.t0TimestampMs,
      graceDeadlineMs: this.graceDeadlineMs,
      graceRemainingMs: this.graceRemainingMs,
      isInGracePeriod: this.isInGracePeriod,
      userSpeaking: this.userSpeaking,
      assistantSpeaking: this.assistantSpeaking,
      userNaturalWrapMode: this.userNaturalWrapMode,
    };
  }
}

export default RotationStore;
